import { jsx, jsxs } from 'react/jsx-runtime';
import { useQueries } from '@tanstack/react-query';
import { resourceQueryKeys } from '../../../resources/queries/resourceQueryKeys.js';
import { useContextAccessToken } from '../../../permission/contexts/AnonShareTokenContext.js';
import { useWoody } from '../../../woody/woody.js';
import { ResourcePreview } from '../../../resources/components/ResourcePreview/ResourcePreview.js';
import { LayoutGroup } from 'framer-motion';
import { ResourcesPreviewUiConfigProvider } from '../../../resources/components/ResourcePreview/resourcePreviewUiConfigContext.js';
import { ResourcePreviewSkeleton } from '../../../resources/components/ResourcePreview/ResourcePreviewSkeleton/ResourcePreviewSkeleton.js';
import { styled } from 'styled-components';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { RESOURCE_PREVIEW_GRID_COMPACT_CARD_DIMENSIONS } from '@fabric-app/ui/theme/resourcePreviewConstants';
import { cssTextEllipsis } from '@fabric-app/ui/constants/styled-utils';
import { useShowExpandedResource } from '../../../resources/stores/useExpandedResourceStore.js';
import { randomInt } from '@fabric-app/utils/random';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useAssistantStore } from '../../stores/assistantStore.js';
import { v4 } from 'uuid';

const ChatbotMessageReferencedItems = ({ data: _data }) => {
  const [id] = useState(v4());
  const { client } = useWoody();
  const accessToken = useContextAccessToken();
  const setChatAssistantFullscreen = useAssistantStore((s) => s.setChatAssistantFullscreen);
  const data = useMemo(() => {
    return Object.values(_data.reduce((acc, ref) => {
      acc[ref.resourceId] = ref;
      return acc;
    }, {}));
  }, [
    _data
  ]);
  const resourceQueries = useQueries({
    queries: data.map((ref) => {
      return {
        queryKey: resourceQueryKeys.resourceDetail(ref.resourceId),
        meta: {
          chatbotReference: ref
        },
        queryFn: async () => {
          const res = await client.v2({
            endpoint: "/v2/resources/{resourceId}",
            params: {
              resourceId: ref.resourceId
            }
          }, {
            query: {
              accessToken,
              includeHidden: true
            }
          });
          return res;
        }
      };
    })
  });
  const showExpandedResource = useShowExpandedResource();
  const resources = resourceQueries.filter((q) => !q.error).map((q) => q.data);
  const containerRef = useRef(null);
  const [offset, setScrollLeftOffset] = useState(void 0);
  useEffect(() => {
    const messageListEl = document.querySelector("[data-assistant-message-list]");
    const horizontalContainer = containerRef?.current;
    const calcOffset = () => {
      if (messageListEl && horizontalContainer) {
        const horizontalContainerRect = horizontalContainer.getBoundingClientRect();
        const messageListElRect = messageListEl.getBoundingClientRect();
        setScrollLeftOffset({
          left: Math.round(horizontalContainerRect.left - messageListElRect.left),
          right: Math.round(messageListElRect.right - horizontalContainerRect.right)
        });
      }
    };
    calcOffset();
    window.addEventListener("resize", calcOffset);
    const resizeObserver = new ResizeObserver(calcOffset);
    if (messageListEl) {
      resizeObserver.observe(messageListEl);
    }
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", calcOffset);
    };
  }, [
    containerRef
  ]);
  if (resources.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(ResourcesPreviewUiConfigProvider, {
    uiContext: "assistant",
    children: /* @__PURE__ */ jsx("div", {
      ref: containerRef,
      children: /* @__PURE__ */ jsx(ScrollArea.Default, {
        scrollbarVariant: "none",
        orientation: "horizontal",
        style: {
          paddingTop: 10,
          marginLeft: -22 - (offset?.left || 0),
          width: `calc(100% + 22px + ${(offset?.left || 0) + (offset?.right || 0)}px)`
        },
        children: /* @__PURE__ */ jsx(LayoutGroup, {
          id,
          children: !!offset && /* @__PURE__ */ jsx(ItemsContainer, {
            style: {
              paddingLeft: 22 + (offset.left || 0),
              paddingRight: 22 + (offset.right || 0) / 2
            },
            children: resources.map((resource, index) => {
              const ref = data.find((d) => d.resourceId === resource?.id);
              if (!ref) {
                return null;
              }
              return /* @__PURE__ */ jsxs(ItemContainer, {
                children: [
                  ref.label && /* @__PURE__ */ jsx(PreviewLabel, {
                    children: ref.label
                  }),
                  resource ? /* @__PURE__ */ jsx(ResourcePreview, {
                    resource,
                    onClickResource: () => {
                      const valueWithOffset = Number(ref.value) + randomInt(100, 999) / 1e6;
                      const queryParams = ref.type === "time" ? {
                        t: valueWithOffset,
                        p: void 0
                      } : ref.type === "page" ? {
                        p: valueWithOffset,
                        t: void 0
                      } : {};
                      showExpandedResource(resource.id, {
                        tab: "assistant",
                        storeCurrentAsPrevious: true,
                        queryParams
                      });
                      setChatAssistantFullscreen(false);
                    }
                  }, resource.id) : /* @__PURE__ */ jsx(ResourcePreviewSkeleton, {
                    viewMode: "Grid"
                  })
                ]
              }, resource?.id + "--" + index);
            })
          })
        })
      })
    })
  });
};
const ItemsContainer = styled.div.withConfig({
  displayName: "ChatbotMessageReferencedItems__ItemsContainer",
  componentId: "sc-3f019ab4-0"
})([
  `display:flex;gap:16px;height:`,
  `px;margin-top:10px;padding-left:20px;padding-right:20px;`
], RESOURCE_PREVIEW_GRID_COMPACT_CARD_DIMENSIONS.height);
const ItemContainer = styled.div.withConfig({
  displayName: "ChatbotMessageReferencedItems__ItemContainer",
  componentId: "sc-3f019ab4-1"
})([
  `width:`,
  `px;height:`,
  `px;position:relative;`
], RESOURCE_PREVIEW_GRID_COMPACT_CARD_DIMENSIONS.width, RESOURCE_PREVIEW_GRID_COMPACT_CARD_DIMENSIONS.height);
const PreviewLabel = styled.div.withConfig({
  displayName: "ChatbotMessageReferencedItems__PreviewLabel",
  componentId: "sc-3f019ab4-2"
})([
  `position:absolute;z-index:10;top:-5px;left:2px;padding:0 5px;border-radius:6px;background:#6d6d6d;max-width:90%;`,
  `;`
], cssTextEllipsis);

export { ChatbotMessageReferencedItems };
//# sourceMappingURL=ChatbotMessageReferencedItems.js.map
